import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, MapPin, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import CommuteRouteMap from "@/components/CommuteRouteMap";
import { geocodeBackend } from "@/lib/maps";

const DAYS = [
  { id: "mon", label: "Lun" },
  { id: "tue", label: "Mar" },
  { id: "wed", label: "Mer" },
  { id: "thu", label: "Gio" },
  { id: "fri", label: "Ven" },
  { id: "sat", label: "Sab" },
  { id: "sun", label: "Dom" },
];

const empty = {
  route_name: "",
  start_address: "",
  start_lat: null,
  start_lng: null,
  end_address: "",
  end_lat: null,
  end_lng: null,
  usual_days: [],
  usual_time_window: "",
  max_detour_km: 5,
  active: true,
};

export default function DriverRoutes() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = none, 'new', or id
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/driver/routes");
      setRoutes(data);
      // Phase 39 — Auto-geocoding silenzioso: per tragitti senza coordinate
      // proviamo a geocodificarle dagli indirizzi salvati e a salvarle.
      const missing = data.filter(
        (r) =>
          (!(typeof r.start_lat === "number") || !(typeof r.start_lng === "number") ||
           !(typeof r.end_lat === "number") || !(typeof r.end_lng === "number")) &&
          r.start_address && r.end_address
      );
      if (missing.length > 0) {
        // Geocoding non-blocking, sequenziale per non saturare il rate limit
        (async () => {
          let anyUpdated = false;
          for (const r of missing) {
            try {
              const needStart = !(typeof r.start_lat === "number" && typeof r.start_lng === "number");
              const needEnd = !(typeof r.end_lat === "number" && typeof r.end_lng === "number");
              const gs = needStart ? await geocodeBackend(r.start_address) : null;
              const ge = needEnd ? await geocodeBackend(r.end_address) : null;
              const patch = {};
              if (gs?.found) { patch.start_lat = gs.lat; patch.start_lng = gs.lng; }
              if (ge?.found) { patch.end_lat = ge.lat; patch.end_lng = ge.lng; }
              if (Object.keys(patch).length > 0) {
                await api.put(`/driver/routes/${r.id}`, { ...r, ...patch });
                anyUpdated = true;
              }
            } catch {
              // silent: best-effort
            }
          }
          if (anyUpdated) {
            try {
              const { data: refreshed } = await api.get("/driver/routes");
              setRoutes(refreshed);
            } catch { /* ignore */ }
          }
        })();
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    if (routes.length >= 3) return toast.error("Massimo 3 tragitti abituali");
    setForm(empty);
    setEditing("new");
  };

  const startEdit = (r) => {
    setForm({
      route_name: r.route_name,
      start_address: r.start_address,
      start_lat: typeof r.start_lat === "number" ? r.start_lat : null,
      start_lng: typeof r.start_lng === "number" ? r.start_lng : null,
      end_address: r.end_address,
      end_lat: typeof r.end_lat === "number" ? r.end_lat : null,
      end_lng: typeof r.end_lng === "number" ? r.end_lng : null,
      usual_days: r.usual_days || [],
      usual_time_window: r.usual_time_window || "",
      max_detour_km: typeof r.max_detour_km === "number" ? r.max_detour_km : 5,
      active: r.active,
    });
    setEditing(r.id);
  };

  const toggleDay = (d) => {
    setForm((s) => ({
      ...s,
      usual_days: s.usual_days.includes(d) ? s.usual_days.filter((x) => x !== d) : [...s.usual_days, d],
    }));
  };

  const save = async () => {
    if (!form.route_name.trim() || !form.start_address.trim() || !form.end_address.trim()) {
      return toast.error("Compila nome, partenza e arrivo");
    }
    const payload = {
      ...form,
      max_detour_km: Number.isFinite(+form.max_detour_km) ? Math.max(0, Math.min(50, +form.max_detour_km)) : 5,
    };
    setBusy(true);
    try {
      // Auto-geocoding: arricchisce start_lat/lng + end_lat/lng se mancano,
      // così la mini-mappa può renderizzare il tragitto reale.
      const needStart = !(typeof form.start_lat === "number" && typeof form.start_lng === "number");
      const needEnd = !(typeof form.end_lat === "number" && typeof form.end_lng === "number");
      if (needStart || needEnd) {
        const [gs, ge] = await Promise.all([
          needStart ? geocodeBackend(form.start_address.trim()) : Promise.resolve(null),
          needEnd ? geocodeBackend(form.end_address.trim()) : Promise.resolve(null),
        ]);
        if (gs?.found) { payload.start_lat = gs.lat; payload.start_lng = gs.lng; }
        if (ge?.found) { payload.end_lat = ge.lat; payload.end_lng = ge.lng; }
      }
      if (editing === "new") await api.post("/driver/routes", payload);
      else await api.patch(`/driver/routes/${editing}`, payload);
      toast.success("Tragitto salvato");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo tragitto?")) return;
    try {
      await api.delete(`/driver/routes/${id}`);
      toast.success("Tragitto eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/driver/routes/${r.id}`, { active: !r.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;

  return (
    <div className="space-y-5" data-testid="driver-routes-page">
      <Link to="/driver/profile" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
        <ArrowLeft className="h-4 w-4" /> Profilo
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Commuter Delivery Network</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">I tuoi tragitti pendolari</h1>
          <p className="text-dapnow-muted text-sm mt-2 max-w-2xl">
            Aggiungi fino a 3 tragitti che fai abitualmente in Ticino o nel Grigioni italiano (es. casa → lavoro,
            lavoro → casa). Ti proporremo solo ordini <strong>Shared</strong> compatibili con il tuo percorso e
            la deviazione massima che accetti — guadagni senza cambiare le tue abitudini.
          </p>
        </div>
        {routes.length < 3 && editing === null && (
          <button onClick={startNew} data-testid="new-route-btn" className="btn-primary">
            <Plus className="h-4 w-4 mr-1.5" /> Aggiungi tragitto
          </button>
        )}
      </div>

      {editing !== null && (
        <section className="card-soft p-5 sm:p-7 space-y-4" data-testid="route-form">
          <h2 className="font-display text-lg font-bold tracking-tight">{editing === "new" ? "Nuovo tragitto" : "Modifica tragitto"}</h2>
          <div>
            <label className="text-sm font-semibold tracking-tight">Nome</label>
            <input data-testid="route-name" value={form.route_name} onChange={(e) => setForm((s) => ({ ...s, route_name: e.target.value }))} className="input-tall mt-1.5 w-full" placeholder="Es. Chiasso → Lugano mattina" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold tracking-tight">Partenza</label>
              <input data-testid="route-start" value={form.start_address} onChange={(e) => setForm((s) => ({ ...s, start_address: e.target.value, start_lat: null, start_lng: null }))} className="input-tall mt-1.5 w-full" placeholder="Chiasso" />
            </div>
            <div>
              <label className="text-sm font-semibold tracking-tight">Arrivo</label>
              <input data-testid="route-end" value={form.end_address} onChange={(e) => setForm((s) => ({ ...s, end_address: e.target.value, end_lat: null, end_lng: null }))} className="input-tall mt-1.5 w-full" placeholder="Lugano" />
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold tracking-tight">Giorni abituali</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {DAYS.map((d) => {
                const active = form.usual_days.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggleDay(d.id)}
                    data-testid={`route-day-${d.id}`}
                    className={`h-10 px-4 rounded-xl text-sm font-semibold transition ${active ? "bg-dapnow-blue text-white" : "bg-white border border-dapnow-line text-dapnow-ink hover:border-dapnow-blue"}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold tracking-tight">Fascia oraria (opz.)</label>
            <input data-testid="route-time" value={form.usual_time_window} onChange={(e) => setForm((s) => ({ ...s, usual_time_window: e.target.value }))} className="input-tall mt-1.5 w-full" placeholder="08:00-10:00" />
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-sm font-semibold tracking-tight">Deviazione massima accettata</label>
              <span className="font-display text-base font-bold tracking-tight">
                {form.max_detour_km} <span className="text-xs text-dapnow-muted font-medium">km</span>
              </span>
            </div>
            <input
              type="range"
              min={0} max={50} step={1}
              value={form.max_detour_km}
              onChange={(e) => setForm((s) => ({ ...s, max_detour_km: +e.target.value }))}
              data-testid="route-max-detour-km"
              className="mt-2 w-full accent-dapnow-blue"
            />
            <p className="text-xs text-dapnow-muted mt-1">
              Quanti km sei disposto a deviare dal tuo tragitto per un ritiro o una consegna. Default 5 km.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm((s) => ({ ...s, active: e.target.checked }))} data-testid="route-active" />
            Tragitto attivo
          </label>
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => setEditing(null)} className="btn-ghost">Annulla</button>
            <button onClick={save} disabled={busy} data-testid="route-save-btn" className="btn-primary">
              {busy ? "Salvo…" : "Salva tragitto"}
            </button>
          </div>
        </section>
      )}

      {routes.length === 0 && editing === null ? (
        <div className="card-soft p-10 text-center">
          <MapPin className="h-6 w-6 text-dapnow-blue mx-auto" />
          <p className="mt-3 font-display text-lg font-semibold tracking-tight">Nessun tragitto inserito</p>
          <p className="text-sm text-dapnow-muted mt-1 max-w-md mx-auto">
            Aggiungi i tuoi percorsi pendolari abituali (casa ↔ lavoro, scuola, palestra…) per ricevere
            proposte di consegne Shared lungo la tua strada — solo Ticino e Grigioni italiano.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {routes.map((r) => (
            <div key={r.id} className="card-soft p-5" data-testid={`route-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="overline">Il tuo tragitto pendolare</p>
                  <h3 className="font-display text-lg font-semibold tracking-tight">{r.route_name}</h3>
                </div>
                <button onClick={() => toggleActive(r)} className={`pill ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-dapnow-bg text-dapnow-muted"}`}>
                  {r.active ? "Attivo" : "Pausa"}
                </button>
              </div>

              {/* Phase 31 — mini-map del tragitto pendolare con cerchio max_detour_km */}
              <div className="mt-4">
                <CommuteRouteMap
                  startLat={r.start_lat}
                  startLng={r.start_lng}
                  endLat={r.end_lat}
                  endLng={r.end_lng}
                  startLabel={r.start_address}
                  endLabel={r.end_address}
                  maxDetourKm={typeof r.max_detour_km === "number" ? r.max_detour_km : 5}
                />
              </div>

              <p className="mt-3 text-xs text-dapnow-muted leading-relaxed">
                DapNow usa questo percorso per proporti consegne compatibili con la strada che fai già.
              </p>

              <p className="mt-3 text-sm">
                <span className="text-dapnow-muted">Da</span> <span className="font-medium">{r.start_address}</span><br />
                <span className="text-dapnow-muted">A</span> <span className="font-medium">{r.end_address}</span>
              </p>
              {(r.usual_days?.length || r.usual_time_window) && (
                <p className="mt-2 text-xs text-dapnow-muted">
                  {r.usual_days?.length ? r.usual_days.map((d) => DAYS.find((x) => x.id === d)?.label).join(", ") : "Tutti i giorni"}
                  {r.usual_time_window ? ` · ${r.usual_time_window}` : ""}
                </p>
              )}
              <p className="mt-2 text-xs text-dapnow-muted">
                Deviazione massima: <strong className="text-dapnow-ink font-semibold">{typeof r.max_detour_km === "number" ? r.max_detour_km : 5} km</strong>
              </p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => startEdit(r)} data-testid={`route-edit-${r.id}`} className="text-sm font-semibold text-dapnow-blue inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Modifica</button>
                <button onClick={() => remove(r.id)} data-testid={`route-delete-${r.id}`} className="text-sm font-semibold text-dapnow-red inline-flex items-center gap-1 ml-auto"><Trash2 className="h-3.5 w-3.5" /> Elimina</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {routes.length > 0 && editing === null && (
        <p className="text-xs text-dapnow-muted text-center max-w-2xl mx-auto" data-testid="commute-accuracy-note">
          💡 Più il tragitto è preciso, più riceverai ordini realmente compatibili.
        </p>
      )}
    </div>
  );
}
